import "../App.css";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Warning from "../components/Warning";
import React, { useEffect, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";

const SECTIONS = [
  {
    id: "introduction",
    title: "1. Introduction",
    content: (
      <>
        <p>
          Welcome to the personal portfolio website of Jack Frederick (csfrederick.com, Wall of Polaroids, Foredark Games). We respect your privacy and are committed to protecting any personal data we process.
        </p>
        <p>
          This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website, use our self-hosted applications, play our games, or contact us.
        </p>
      </>
    ),
  },
  {
    id: "info-collection",
    title: "2. Information We Collect",
    content: (
      <>
        <p className="bold" style={{ color: "var(--homeThick)" }}>A. Voluntarily Provided Information</p>
        <p>
          When you use our Contact page or reach out for whitelisting or support, you may provide details such as your name, email address, phone number, and message content.
        </p>
        <p className="bold" style={{ color: "var(--homeThick)" }}>B. Automatically Collected Information</p>
        <p>
          Like most self-hosted websites, our server logs automatically collect your IP address, browser type, operating system, and referral URLs for security and operational purposes.
        </p>
        <p>
          We also use <strong>Google Analytics 4 (GA4)</strong> to understand how users interact with our site. GA4 collects anonymous usage metrics, page views, and navigation flows.
        </p>
      </>
    ),
  },
  {
    id: "info-use",
    title: "3. How We Use Your Information",
    content: (
      <>
        <p>We use the collected information for the following purposes:</p>
        <ul style={{ textAlign: "left", paddingLeft: "20px", color: "var(--homeMid)" }}>
          <li style={{ marginBottom: "8px" }}>To respond to job inquiries, bug reports, and whitelist requests.</li>
          <li style={{ marginBottom: "8px" }}>To run and maintain our self-hosted services, including web and game servers.</li>
          <li style={{ marginBottom: "8px" }}>To analyze traffic metrics via Google Analytics and improve website navigation.</li>
          <li style={{ marginBottom: "8px" }}>To ensure security, detect fraud, and troubleshoot issues.</li>
        </ul>
      </>
    ),
  },
  {
    id: "cookies",
    title: "4. Cookies and Analytics",
    content: (
      <>
        <p>
          We use cookies to facilitate analytics tracking. A cookie is a small file stored on your browser.
        </p>
        <p>
          You can choose to disable cookies through your individual browser options. However, please note that disabling cookies may affect how you interact with this and other websites.
        </p>
        <p>
          For more information on Google Analytics privacy policies, please visit the Google Privacy & Terms site.
        </p>
      </>
    ),
  },
  {
    id: "data-security",
    title: "5. Data Security & Hosting",
    content: (
      <>
        <p>
          This website is home-made and self-hosted on a private home server network. We take administrative and technical precautions to safeguard the server against unauthorized access or modification.
        </p>
        <p>
          Please remember that no method of transmission over the internet or method of electronic storage is 100% secure, and we cannot guarantee absolute security.
        </p>
      </>
    ),
  },
  {
    id: "user-rights",
    title: "6. Your Rights & Contact Details",
    content: (
      <>
        <p>
          You have the right to request access to any personal data we hold about you (such as your contact emails), request corrections, or ask for its permanent deletion.
        </p>
        <p>
          If you have any questions about this Privacy Policy or wish to exercise your rights, please contact Jack Frederick at:
        </p>
        <p style={{ marginTop: "12px" }}>
          <a id="privacy-email-link" className="privacy-highlight-link" href="mailto:jack@csfrederick.com">
            jack@csfrederick.com
          </a>
        </p>
      </>
    ),
  },
];

const Privacy = () => {
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      let current = "";
      for (const section of SECTIONS) {
        const el = document.getElementById(section.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 180) {
            current = section.id;
          }
        }
      }
      setActiveSection(current || SECTIONS[0].id);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="App" style={{ margin: "-8px" }}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(22px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        .privacy-enter-title {
          animation: fadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
          animation-delay: 0.05s;
        }
        .privacy-enter-subtitle {
          animation: fadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
          animation-delay: 0.18s;
        }
        .privacy-enter-layout {
          animation: fadeUp 0.85s cubic-bezier(0.22, 1, 0.36, 1) both;
          animation-delay: 0.35s;
        }

        /* Glassmorphic Cards */
        .privacy-card {
          position: relative;
          border-radius: 14px;
          padding: 24px 28px;
          margin-bottom: 24px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(160, 110, 255, 0.12);
          transition: border-color 0.25s ease,
                      box-shadow 0.25s ease,
                      transform 0.22s cubic-bezier(0.22, 1, 0.36, 1);
          text-align: left;
        }
        .privacy-card:hover {
          border-color: rgba(160, 110, 255, 0.35);
          box-shadow: 0 0 28px 4px rgba(150, 100, 255, 0.1),
                      0 4px 24px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }
        /* Glow behind card on hover */
        .privacy-card::before {
          content: '';
          position: absolute;
          inset: -12px;
          border-radius: 20px;
          background: radial-gradient(ellipse at 50% 50%, rgba(150, 100, 255, 0.08) 0%, transparent 70%);
          filter: blur(12px);
          z-index: -1;
          opacity: 0;
          transition: opacity 0.25s ease;
          pointer-events: none;
        }
        .privacy-card:hover::before {
          opacity: 1;
        }

        /* Layout styles */
        .privacy-container {
          display: flex;
          gap: 40px;
          max-width: 1000px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .privacy-sidebar {
          width: 250px;
          position: sticky;
          top: 100px;
          height: fit-content;
          text-align: left;
        }

        @media (max-width: 800px) {
          .privacy-container {
            flex-direction: column;
          }
          .privacy-sidebar {
            width: 100%;
            position: relative;
            top: 0;
            margin-bottom: 20px;
          }
        }

        .toc-title {
          font-size: 1.1rem;
          color: var(--homeThick);
          margin-bottom: 12px;
          font-weight: bold;
        }

        .toc-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .toc-link {
          color: var(--homeSM);
          text-decoration: none;
          font-size: 0.9rem;
          opacity: 0.6;
          transition: opacity 0.2s ease, color 0.2s ease, padding-left 0.2s ease;
          display: inline-block;
        }
        .toc-link.active {
          opacity: 1;
          color: rgb(170, 130, 255);
          text-shadow: 0 0 8px rgba(170, 130, 255, 0.4);
          padding-left: 6px;
        }
        .toc-link:hover {
          opacity: 0.9;
          color: var(--overLin);
        }

        .privacy-highlight-link {
          color: rgb(180, 140, 255) !important;
          text-shadow: 0 0 10px rgba(180, 140, 255, 0.3);
          transition: opacity 0.2s ease, text-shadow 0.2s ease;
        }
        .privacy-highlight-link:hover {
          opacity: 0.8;
          text-shadow: 0 0 15px rgba(180, 140, 255, 0.6);
        }

        .privacy-content {
          flex: 1;
        }

        .privacy-card-title {
          font-size: 1.3rem;
          color: var(--homeThick);
          margin-top: 0;
          margin-bottom: 16px;
        }
      `}</style>

      <HelmetProvider>
        <Helmet>
          <title>Privacy Policy - Jack Frederick</title>
          <meta
            name="Privacy Policy - Jack Frederick"
            content="Privacy Policy and data practices for csfrederick.com."
            description="Privacy Policy and data practices for csfrederick.com."
            author="Jack Frederick"
          />
        </Helmet>
      </HelmetProvider>

      <Warning />
      <Navbar />

      <header className="Home">
        <br />
        <div className="screenBox Minsize">
          <p className="Homethick privacy-enter-title">Privacy Policy</p>
          <p className="Homemid privacy-enter-subtitle" style={{ fontSize: "1.1rem", opacity: 0.7 }}>
            Last Updated: July 2, 2026
          </p>
          <br /><br />

          <div className="privacy-container privacy-enter-layout">
            <nav className="privacy-sidebar" aria-label="Table of Contents">
              <p className="toc-title">Table of Contents</p>
              <ul className="toc-list">
                {SECTIONS.map((section) => (
                  <li key={section.id}>
                    <a
                      id={`toc-${section.id}`}
                      href={`#${section.id}`}
                      className={`toc-link ${activeSection === section.id ? "active" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        const target = document.getElementById(section.id);
                        if (target) {
                          target.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
              <br /><br />
              <a
                id="back-home-button"
                className="Homesmall ats"
                style={{ color: "black", textShadow: "none", display: "inline-block" }}
                href="/"
              >
                Go Back Home
              </a>
            </nav>

            <div className="privacy-content">
              {SECTIONS.map((section) => (
                <section
                  key={section.id}
                  id={section.id}
                  className="privacy-card"
                >
                  <h2 className="privacy-card-title">{section.title}</h2>
                  <div style={{ color: "var(--homeMid)", lineHeight: "1.6" }}>
                    {section.content}
                  </div>
                </section>
              ))}
            </div>
          </div>

          <br /><br /><br />
          <div className="photoSpacer" />
          <div className="photoSpacer" />
        </div>
      </header>

      <Footer />
    </div>
  );
};

export default Privacy;
